import React from 'react';
import { MODULES, PERMISSION_LEVELS } from '../../utils/constants';
import type { Tokens } from '../../theme/tokens';

// Module-by-module None / Read Only / Full Access, with "set all" buttons.
// Used for a user's permissions and for a role's default permissions.

export const MODULE_IDS = MODULES.map((m) => m.id);

export type Permissions = Record<string, string>;

export const noPermissions = (): Permissions =>
  MODULE_IDS.reduce((acc, id) => ({ ...acc, [id]: 'None' }), {});

/** A stored JSON permission map, filled out to every module (missing = None). */
export const parsePermissions = (raw?: string | null): Permissions => {
  try {
    return { ...noPermissions(), ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return noPermissions();
  }
};

interface PermissionGridProps {
  title: React.ReactNode;
  permissions: Permissions;
  onChange: (next: Permissions) => void;
  t: Tokens;
}

const PermissionGrid = ({ title, permissions, onChange, t }: PermissionGridProps) => {
  const setAll = (level: string) =>
    onChange(MODULE_IDS.reduce((acc, id) => ({ ...acc, [id]: level }), {}));

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        borderBottom: `2px solid ${t.border}`, paddingBottom: '8px', marginBottom: '15px', flexWrap: 'wrap', gap: '10px'
      }}>
        <h4 style={{ margin: 0, color: t.textMain }}>{title}</h4>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {PERMISSION_LEVELS.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() => setAll(level.value)}
              style={{
                padding: '6px 12px', borderRadius: t.radius.sm, cursor: 'pointer',
                fontWeight: 'bold', fontSize: t.fontSize.sm,
                border: `1px solid ${level.color}`, color: level.color, backgroundColor: 'transparent'
              }}
            >
              {level.value === 'None' ? 'Clear All' : level.value === 'Read Only' ? 'All Read-Only' : 'Grant Full Access'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
        {MODULES.map((mod) => (
          <div key={mod.id} style={{
            backgroundColor: t.bgSubtle, padding: '12px 15px',
            borderRadius: t.radius.lg, border: `1px solid ${t.border}`,
            display: 'flex', flexDirection: 'column', gap: '5px'
          }}>
            <div style={{
              fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md,
              borderBottom: `1px solid ${t.border}`, paddingBottom: '5px', marginBottom: '5px'
            }}>
              {mod.title}
            </div>
            <div style={{ display: 'flex', gap: '5px' }}>
              {PERMISSION_LEVELS.map((level) => {
                const selected = permissions[mod.id] === level.value;
                return (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => onChange({ ...permissions, [mod.id]: level.value })}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: t.radius.sm, cursor: 'pointer',
                      fontWeight: 'bold', fontSize: t.fontSize.sm, transition: 'all 0.2s ease',
                      border: `1px solid ${level.color}`,
                      backgroundColor: selected ? level.color : 'transparent',
                      color: selected ? '#FFF' : level.color
                    }}
                  >
                    {level.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PermissionGrid;
